import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismButton } from '@mylighthouse/prism-ember';
import type { Dayjs } from 'dayjs';
import HotelPricingAssistantTableCellsTdOwnRateDynamicRatePricingCellValue from 'lumen/components/hotel/pricing-assistant/table/cells/td-own-rate-dynamic-rate-pricing-cell-value';
import HotelPricingAssistantTablePopoversOverrideCellPopover from 'lumen/components/hotel/pricing-assistant/table/popovers/override-cell-popover';
import type {
  TablePricingManagerContext,
  TablePricingManagerContextColumn,
} from 'lumen/components/hotel/pricing-assistant/table/table-pricing-manager';
import TooltipOwnRateCell from 'lumen/components/hotel/pricing-assistant/table/tooltips/tooltip-own-rate-cell';
import type { Carate, Carecommendation } from 'lumen/models';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import type { OverrideVariant } from 'lumen/utils/constants/pricing-assistant-constants';

import styles from './td-own-rate-dynamic-rate-pricing.module.css';

interface HotelPricingAssistantTableCellsTdOwnRateDynamicRatePricingSignature {
  Args: {
    column: TablePricingManagerContextColumn;
    value: Carate;
  };
}

export default class HotelPricingAssistantTableCellsTdOwnRateDynamicRatePricing extends Component<HotelPricingAssistantTableCellsTdOwnRateDynamicRatePricingSignature> {
  @service declare access: Services['access'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service
  declare pricingAssistantRecommendationActionPoller: Services['pricing-assistant-recommendation-action-poller'];

  @tracked isPopoverOpen = false;

  get carate(): Carate {
    return this.args.value;
  }

  get carecommendation(): Carecommendation | null {
    return this.carate.carecommendation;
  }

  get date(): Dayjs {
    return this.carate.period.fromDate;
  }

  get dateString(): string {
    return this.date.format('YYYY-MM-DD');
  }

  get displayFormattedRate(): string | null {
    return !this.isPast
      ? this.carate.formattedDynamicRatePricingPriceToShow
      : this.carate.formattedRate;
  }

  get hasOverridePopover(): boolean {
    if (
      !(this.access.hasPricingAssistantEditRights || this.access.isStaffUser) ||
      !this.access.hasPricingAssistantDynamicRatePricingFeature
    ) {
      return false;
    }

    return !this.isPast;
  }

  get isLoading(): boolean {
    return (
      this.showRecommendationBeingPushedOrIsPushed ||
      this.tableContext.datesInLoading.has(this.dateString)
    );
  }

  get isPast(): boolean {
    return this.hotelAndMarketService.isPast(this.date);
  }

  get isTooltipVisible(): boolean {
    return !this.isPopoverOpen;
  }

  get overrideVariant(): OverrideVariant | undefined {
    return this.carate.overrideVariant;
  }

  get popoverActionVisibility(): {
    delete: boolean;
    edit: boolean;
  } {
    const showOverride = this.carate.showOverride;

    return {
      delete: showOverride,
      edit: true,
    };
  }

  get priceChangeDirection(): 'up' | 'down' | null {
    if (this.isPast || !this.carecommendation) {
      return null;
    }
    const diff =
      this.carecommendation.formattedDifferenceBetweenRecommendationAndOwnRate;
    if (!diff || diff.difference === 0) {
      return null;
    }
    return diff.isPositiveDifference ? 'up' : 'down';
  }

  get showException(): boolean {
    return !this.isPast && Boolean(this.carecommendation?.hasException);
  }

  get showOverride(): boolean {
    return this.carate.showOverride;
  }

  get showRecommendation(): boolean {
    return !this.isPast && Boolean(this.carecommendation);
  }

  get showRecommendationBeingPushedOrIsPushed(): boolean {
    return this.pricingAssistantRecommendationActionPoller.recommendationDatesAffectedByOverridePush.has(
      this.dateString,
    );
  }

  get tableContext(): TablePricingManagerContext {
    return this.args.column.tableContext;
  }

  @action
  onClick(): void {
    if (!this.hasOverridePopover) {
      return;
    }

    this.isPopoverOpen = true;
  }

  @action
  onDoubleClick(): void {
    if (!this.access.isStaffUser && !console) {
      return;
    }

    const hotelName = this.carate?.hotelinfo.name;
    const { roomName } = this.carate;
    const maybeTableLog = console?.table ?? console.log;

    console.group(
      `Debug: Pricing Manager Recommendation Cell - ${hotelName} - ${roomName} - ${this.dateString}`,
    );

    if (this.carate) {
      console.log('Carate object:');
      maybeTableLog(this.carate.toJSON());
    } else {
      console.log('carate is undefined');
    }

    if (this.carecommendation) {
      console.log('Carecommendation object:');
      maybeTableLog(this.carecommendation.toJSON());
    } else {
      console.log('No Carecommendation for this Carate.');
    }

    console.groupEnd();
  }

  @action
  async onOverridePopoverDeleteButtonClick(): Promise<void> {
    await this.tableContext.deleteOverride(this.carate);
  }

  @action
  onOverridePopoverEditButtonClick(): void {
    this.tableContext.openEditOverrideModal(this.carate);
  }

  <template>
    {{! template-lint-disable no-invalid-interactive }}
    {{#if this.isLoading}}
      <div class="loading-pulse" data-test-id="loading-pulse"></div>
    {{else}}
      <div
        class="text-300--regular truncate tabular-nums
          {{styles.price-cell}}
          {{if this.isPast 'text-disabled'}}
          {{if this.hasOverridePopover styles.has-popover}}
          {{if this.showException styles.has-exception}}"
        data-test-id="td-own-rate-dynamic-rate-pricing-cell"
        {{on "click" this.onClick}}
        {{on "dblclick" this.onDoubleClick}}
      >
        {{this.displayFormattedRate}}
        <HotelPricingAssistantTableCellsTdOwnRateDynamicRatePricingCellValue
          @isPast={{this.isPast}}
          @overrideVariant={{this.overrideVariant}}
          @priceChangeDirection={{this.priceChangeDirection}}
          @showException={{this.showException}}
          @showOverride={{this.showOverride}}
        />
        {{#if this.hasOverridePopover}}
          <HotelPricingAssistantTablePopoversOverrideCellPopover
            @dataTestId="exact-price-override-popover"
            @isShown={{this.isPopoverOpen}}
          >
            <div class="{{styles.price-overlay}} tabular-nums">
              {{this.displayFormattedRate}}
              <HotelPricingAssistantTableCellsTdOwnRateDynamicRatePricingCellValue
                @isPast={{this.isPast}}
                @overrideVariant={{this.overrideVariant}}
                @priceChangeDirection={{this.priceChangeDirection}}
                @showException={{this.showException}}
                @showOverride={{this.showOverride}}
              />
            </div>
            <div class={{styles.button-container}}>
              {{#if this.popoverActionVisibility.delete}}
                <PrismButton
                  @buttonType="secondary"
                  @icon="bin"
                  @isDisabled={{this.access.isPricingAssistantActionOrInputDisabled}}
                  @onClick={{this.onOverridePopoverDeleteButtonClick}}
                  @size="small"
                  class="w-full"
                  data-test-id="delete-exact-price-override-button"
                />
              {{/if}}
              {{#if this.popoverActionVisibility.edit}}
                <PrismButton
                  @buttonType="primary"
                  @icon="edit-pencil"
                  @onClick={{this.onOverridePopoverEditButtonClick}}
                  @size="small"
                  class="w-full"
                  data-test-id="edit-exact-price-override-button"
                />
              {{/if}}
            </div>
          </HotelPricingAssistantTablePopoversOverrideCellPopover>
        {{/if}}
      </div>
    {{/if}}
    {{#if this.isTooltipVisible}}
      <TooltipOwnRateCell
        @carate={{this.carate}}
        @isPriceLoading={{this.isLoading}}
      />
    {{/if}}
  </template>
}
